import { useTheme } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo } from "react";

import { useDataVersion } from "../hooks/useDataVersion";
import { AreaRenderer } from "../renderers/AreaRenderer";
import { BufferedCanvasCache } from "../renderers/core/BufferedCanvasCache";
import type { Size, TransformFunction } from "../renderers/core/types";
import { GridRenderer } from "../renderers/GridRenderer";
import { PlaceRenderer } from "../renderers/PlaceRenderer";
import { PortRenderer } from "../renderers/PortRenderer";
import { RowRenderer } from "../renderers/RowRenderer";
import type {
  AreaBoundary,
  Place,
  PortBoundary,
  RowBoundary,
  ViewState,
} from "../types/warehouse.types";

interface CanvasRendererProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  canvasSize: { width: number; height: number };
  places: Map<string, Place>;
  areaBoundaries: AreaBoundary[];
  rowBoundaries: RowBoundary[];
  portBoundaries: PortBoundary[];
  viewState: ViewState;
  activeLevels: Set<number>;
  activeRows: Set<string>;
  selectedPlaceIds: Set<string>;
  worldToScreen: TransformFunction;
  hoveredArea: AreaBoundary | null;
  hoveredRow: RowBoundary | null;
  showAreaLabels: boolean;
  showRowLabels: boolean;
  showPortLabels: boolean;
  selectedRows: Set<string>;
  hoveredPort: PortBoundary | null;
  selectedPortNames?: Set<string>;
  bufferedCacheRef?: React.MutableRefObject<BufferedCanvasCache | null>;
}

const ConfiguredCanvasRendererComponent: React.FC<CanvasRendererProps> = ({
  canvasRef,
  canvasSize,
  places,
  areaBoundaries,
  rowBoundaries,
  portBoundaries,
  viewState,
  activeLevels,
  activeRows,
  selectedPlaceIds,
  worldToScreen,
  hoveredArea,
  hoveredRow,
  showAreaLabels,
  showRowLabels,
  showPortLabels,
  selectedRows,
  hoveredPort,
  selectedPortNames,
  bufferedCacheRef,
}) => {
  const theme = useTheme();

  const gridRenderer = useMemo(() => new GridRenderer(theme), [theme]);
  const placeRenderer = useMemo(() => new PlaceRenderer(theme), [theme]);
  const areaPolygonRenderer = useMemo(
    () => new AreaRenderer(theme, { showCenterLabel: false }),
    [theme]
  );
  const areaLabelRenderer = useMemo(
    () =>
      new AreaRenderer(theme, {
        showCenterLabel: showAreaLabels,
        fillOpacity: "00",
        borderLineWidth: 0,
      }),
    [showAreaLabels, theme]
  );
  const rowRenderer = useMemo(
    () => new RowRenderer(theme, { showLabel: showRowLabels }),
    [showRowLabels, theme]
  );
  const portRenderer = useMemo(
    () => new PortRenderer(theme, { showLabel: showPortLabels }),
    [showPortLabels, theme]
  );

  const bufferedCache = useMemo(() => new BufferedCanvasCache(), []);
  useEffect(() => () => bufferedCache.dispose(), [bufferedCache]);

  // Expose bufferedCache through ref for hit detection
  useEffect(() => {
    if (bufferedCacheRef) {
      bufferedCacheRef.current = bufferedCache;
    }
  }, [bufferedCache, bufferedCacheRef]);

  const placesVersion = useDataVersion([places, activeLevels, activeRows]);
  const selectionVersion = useDataVersion([selectedPlaceIds]);

  // Use ref to avoid recreating callback on every data change
  const renderPlacesCallbackRef = useCallback(
    (
      offCtx: CanvasRenderingContext2D,
      bufferedWorldToScreen: TransformFunction,
      bufferedSize: Size
    ) => {
      placeRenderer.render(
        offCtx,
        places,
        viewState,
        bufferedWorldToScreen,
        selectedPlaceIds,
        activeLevels,
        activeRows,
        bufferedSize
      );
    },
    [placeRenderer, places, viewState, selectedPlaceIds, activeLevels, activeRows]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    placeRenderer.notifyDataChanged();
    placeRenderer.notifyViewportChanged();
    placeRenderer.notifySelectionChanged();

    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);

    gridRenderer.render(ctx, canvasSize.width, canvasSize.height, viewState);

    areaPolygonRenderer.render(ctx, areaBoundaries, viewState, worldToScreen, hoveredArea);

    rowRenderer.render(
      ctx,
      rowBoundaries,
      viewState,
      worldToScreen,
      hoveredRow,
      places,
      areaBoundaries,
      selectedRows,
      canvasSize
    );

    portRenderer.render(
      ctx,
      portBoundaries,
      viewState,
      worldToScreen,
      hoveredPort,
      selectedPortNames
    );

    bufferedCache.render(
      ctx,
      viewState,
      canvasSize,
      worldToScreen,
      placesVersion + selectionVersion,
      renderPlacesCallbackRef
    );

    areaLabelRenderer.render(ctx, areaBoundaries, viewState, worldToScreen, hoveredArea);
  }, [
    canvasSize,
    viewState,
    worldToScreen,
    places,
    areaBoundaries,
    rowBoundaries,
    portBoundaries,
    activeLevels,
    activeRows,
    selectedPlaceIds,
    selectedRows,
    selectedPortNames,
    hoveredArea,
    hoveredRow,
    hoveredPort,
    placesVersion,
    selectionVersion,
    // Renderers are stable (useMemo with theme/label deps), but keep them for theme changes
    gridRenderer,
    areaPolygonRenderer,
    rowRenderer,
    portRenderer,
    placeRenderer,
    areaLabelRenderer,
  ]);

  return null;
};

function arePropsEqual(
  prevProps: Readonly<CanvasRendererProps>,
  nextProps: Readonly<CanvasRendererProps>
): boolean {
  // Quick checks for primitive values
  if (
    prevProps.showAreaLabels !== nextProps.showAreaLabels ||
    prevProps.showRowLabels !== nextProps.showRowLabels ||
    prevProps.showPortLabels !== nextProps.showPortLabels
  ) {
    return false;
  }

  // Check canvas size
  if (
    prevProps.canvasSize.width !== nextProps.canvasSize.width ||
    prevProps.canvasSize.height !== nextProps.canvasSize.height
  ) {
    return false;
  }

  // Check viewState (shallow comparison of key properties)
  if (
    prevProps.viewState.offsetX !== nextProps.viewState.offsetX ||
    prevProps.viewState.offsetY !== nextProps.viewState.offsetY ||
    prevProps.viewState.scale !== nextProps.viewState.scale ||
    prevProps.viewState.rotation !== nextProps.viewState.rotation
  ) {
    return false;
  }

  // Check if data collections changed by reference
  if (
    prevProps.places !== nextProps.places ||
    prevProps.areaBoundaries !== nextProps.areaBoundaries ||
    prevProps.rowBoundaries !== nextProps.rowBoundaries ||
    prevProps.portBoundaries !== nextProps.portBoundaries
  ) {
    return false;
  }

  // Check Sets
  if (
    prevProps.activeLevels !== nextProps.activeLevels ||
    prevProps.activeRows !== nextProps.activeRows ||
    prevProps.selectedPlaceIds !== nextProps.selectedPlaceIds ||
    prevProps.selectedRows !== nextProps.selectedRows ||
    prevProps.selectedPortNames !== nextProps.selectedPortNames
  ) {
    return false;
  }

  // Check hover states
  if (
    prevProps.hoveredArea !== nextProps.hoveredArea ||
    prevProps.hoveredRow !== nextProps.hoveredRow ||
    prevProps.hoveredPort !== nextProps.hoveredPort
  ) {
    return false;
  }

  // All checks passed - props are equal
  return true;
}

// Use React.memo with custom comparison to prevent unnecessary re-renders
const ConfiguredCanvasRenderer = React.memo(ConfiguredCanvasRendererComponent, arePropsEqual);

export default ConfiguredCanvasRenderer;
